import { Link } from 'react-router-dom';
import styles from './CostMeasurement.module.css';

import type { StorageMeasurement } from './CostOverview';

export interface Measurement {
  compute: { jobs: number; measuredJobs: number; runHours: number; machineHours: number; lookupError: string | null };
  storage: StorageMeasurement;
  viewing: {
    reportedSessions: number; estimatedSessions: number; perSessionRequests: number; perSessionGb: number;
    perSessionSource: string; perSessionSamples: number; viewHours: number; avgVisibleMinutes: number;
  };
}

type Status = 'measured' | 'partial' | 'estimated' | 'entered';
const STATUS_LABEL: Record<Status, string> = {
  measured: 'Measured', partial: 'Partly measured', estimated: 'Estimated', entered: 'Entered',
};

const statusOf = (share: number): Status => (share >= 0.995 ? 'measured' : share > 0 ? 'partial' : 'estimated');
const num = (n: number, dp = 1) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: dp });
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

const Row = ({ figure, status, children }: { figure: string; status: Status; children: React.ReactNode }) => (
  <div className={styles.row}>
    <div className={styles.figure}>
      <span className={styles.figureName}>{figure}</span>
      <span className={`${styles.chip} ${styles[status]}`}>{STATUS_LABEL[status]}</span>
    </div>
    <div className={styles.detail}>{children}</div>
  </div>
);

export const MeasurementPanel = ({ m }: { m: Measurement }) => {
  const { compute, storage, viewing } = m;
  const sessions = viewing.reportedSessions + viewing.estimatedSessions;
  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>How these figures are measured</h3>
      <p className={styles.note}>
        AWS bills the account, not the user, so every figure here is built from our own
        measurements. Where one is missing, the figure falls back to the estimate it replaced.
      </p>
      <div className={styles.rows}>
        <Row figure="Conversion compute" status={compute.jobs ? statusOf(compute.measuredJobs / compute.jobs) : 'estimated'}>
          <p>
            Machine time is measured for {num(compute.measuredJobs, 0)} of {num(compute.jobs, 0)} jobs.
            That is each Batch machine&apos;s EC2 time from launch to termination, including boot,
            image pull and idle minutes, split over the jobs it ran:
            {' '}{num(compute.machineHours, 2)} machine-hours.
            Jobs without it are priced at runtime × the per-job lane rate. That covers jobs from
            before tracking started, and jobs whose machine is still running.
          </p>
          {compute.lookupError && (
            <p className={styles.warn}>
              Machine lookups fail ({compute.lookupError}). Give the backend&apos;s IAM identity
              ecs:DescribeContainerInstances and ec2:DescribeInstances; until then each machine&apos;s
              start and end are inferred from its jobs.
            </p>
          )}
        </Row>
        <Row figure="Storage" status={statusOf(storage.coverage)}>
          <p>
            Every upload, conversion and note attachment records the size of each S3 folder it
            wrote, under the project&apos;s owner: {num(storage.folders, 0)} folders
            ({num(storage.recordedGb)} GB on average over this range)
            {storage.lastRecordedAt && `, the latest on ${date(storage.lastRecordedAt)}`}. Each is
            priced for the days it existed, by its archive age.
          </p>
          {storage.estimatedFiles > 0 && (
            <p className={styles.warn}>
              {num(storage.estimatedFiles, 0)} files have no record yet, mostly from before the records
              existed: {num(storage.estimatedGb)} GB estimated as their upload × their type&apos;s factor
              in Unit prices. <Link to="/admin/users" className={styles.link}>Recalculate storage</Link> on
              their owners reads the real sizes from S3.
            </p>
          )}
        </Row>
        <Row figure="Viewing" status={sessions ? statusOf(viewing.reportedSessions / sessions) : 'estimated'}>
          <p>
            {num(viewing.reportedSessions, 0)} viewer sessions reported what they fetched from
            CloudFront, from S3, through the app server, and in map tiles
            {viewing.reportedSessions > 0 && ` (${num(viewing.viewHours)} h on screen, ${num(viewing.avgVisibleMinutes)} min per session)`}.
            {viewing.estimatedSessions > 0 && (
              <>
                {' '}{num(viewing.estimatedSessions, 0)} sessions did not report, because they ran
                an older app build or came before this release. Each counts as
                {' '}{num(viewing.perSessionRequests, 0)} requests and {num(viewing.perSessionGb, 2)} GB:
                {viewing.perSessionSource === 'measured'
                  ? ` the average of ${num(viewing.perSessionSamples, 0)} reported sessions.`
                  : ' the Unit prices, until at least 5 sessions have reported.'}
              </>
            )}
          </p>
        </Row>
        <Row figure="Downloads" status="measured">
          <p>
            Each download link handed out × the file&apos;s size. A cancelled download still counts,
            and a download resumed on the same link counts once.
          </p>
        </Row>
        <Row figure="Fixed / always-on" status="entered">
          <p>
            Fixed $/month from the Unit prices: app servers, their EBS volumes, public IPv4 addresses
            and anything else that runs whether or not jobs do.
          </p>
        </Row>
      </div>
    </div>
  );
};
